import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { CiSearch } from "react-icons/ci";
import { IoClose } from "react-icons/io5";
import { FaChevronDown } from "react-icons/fa";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LuPlus } from "react-icons/lu";
import * as apicall from '../../components/Apicall';
import serviceNames from '../../data/serviceNames.json';

const UserCardSkeleton = () => (
  <div className="card h-100 shadow-sm"
    style={{
      borderRadius: '8px',
      border: '1px solid #E3EBF8',
      display: 'flex',
      flexDirection: 'column'
    }}>
    <div className="position-relative" style={{ padding: '12px', flex: '0 0 auto' }}>
      <Skeleton height={110} style={{ borderRadius: '10px' }} />
    </div>
    <div className="card-body p-3" style={{ flex: '1 1 auto', marginTop: '0px' }}>
      <Skeleton height={24} width="80%" className="mb-2" />
      <Skeleton height={20} width="60%" className="mb-2" />
      <Skeleton height={20} width="70%" className="mb-2" />
      <Skeleton height={20} width="50%" className="mb-2" />
      <Skeleton height={35} width="100%" style={{ borderRadius: '16px' }} />
    </div>
  </div>
);

const Users = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedRole, setSelectedRole] = useState('5');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [vendorNames, setVendorNames] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedVendorName, setSelectedVendorName] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorHasMore, setVendorHasMore] = useState(true);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const ITEMS_PER_PAGE = 30;
  const VENDORS_PER_PAGE = 30;
  const [debouncedVendorSearch, setDebouncedVendorSearch] = useState('');
  const vendorDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchRoles();
    fetchVendorServices();
    setSelectedRole('5');
  }, []);

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setFilteredUsers([]);
    setHasMore(true);
    fetchUsers({
      role: selectedRole,
      status: selectedStatus,
      search: debouncedSearch,
      vendor: selectedVendor,
      page: 1
    });
  }, [selectedRole, selectedStatus, debouncedSearch, selectedVendor]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedVendorSearch(vendorSearchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [vendorSearchTerm]);

  const fetchRoles = async () => {
    try {
      const response = await apicall.get('roles');
      if (response.success && response.data) {
        setRoles(response.data);
      } else {
        console.error('Failed to fetch roles:', response);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchVendorServices = async (page = 1, isLoadMore = false) => {
    try {
      setIsLoadingVendors(true);
      let url = `get_vendor_details?page=${page}&limit=${VENDORS_PER_PAGE}&status=1`;

      if (debouncedVendorSearch) {
        url += `&name=${encodeURIComponent(debouncedVendorSearch.trim())}`;
      }

      const response = await apicall.get(url);
      if (response && response.data) {
        const newVendors = response.data.map(vendor => ({
          id: vendor.vendor_id,
          name: `${vendor.first_name} ${vendor.last_name}`.trim(),
          service: vendor.service,
          service_name: vendor.service_name
        }));

        setVendorNames(prev => isLoadMore ? [...prev, ...newVendors] : newVendors);
        setVendorHasMore(newVendors.length === VENDORS_PER_PAGE);
        setVendorPage(page);
      }
    } catch (error) {
      console.error('Error fetching vendor services:', error);
    } finally {
      setIsLoadingVendors(false);
    }
  };

    const getServiceName = (names,serviceId) => {
      const service = serviceNames.service_names.find(s => s.service_id === Number(serviceId)); // Default to 7 for doctors
      return service ? service[names] : '';
    };

  const fetchUsers = async (filters = {}) => {
    if (filters.page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let url = `get_service_providers?limit=${ITEMS_PER_PAGE}&page=${filters.page || 1}&role=5`;
      if (filters.status && filters.status !== '') {
        url += `&is_active=${filters.status}`;
      }
      if (filters.search && filters.search.trim() !== '') {
        url += `&name=${encodeURIComponent(filters.search.trim())}`;
      }
      if (filters.vendor && filters.vendor !== '') {
        url += `&vendor_id=${filters.vendor}`;
      }

      const response = await apicall.get(url);

      if (response && response.status === 200) {
        const formattedUsers = response.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name || ''}`.trim(),
          email: user.email,
          phone: user.phone_number || '',
          role: user.role,
          serviceName : getServiceName('name', user.service),
          serviceShortName: getServiceName('shortName', user.service),
          role_name: user.role_name,
          is_active: user.is_active || false,
          join_date: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A',
          profile_image: user.profile_image,
          vendor_name: user.vendor_name || 'N/A'
        }));

        if (filters.page === 1) {
          setUsers(formattedUsers);
          setFilteredUsers(formattedUsers);
        } else {
          setUsers(prev => [...prev, ...formattedUsers]);
          setFilteredUsers(prev => [...prev, ...formattedUsers]);
        }

        // Update pagination metadata from response
        if (response.pagination) {

          setTotalUsers(response.pagination.total || 0);
          setTotalPages(response.pagination.total_pages || 1);
          setHasMore(filters.page < (response.pagination.total_pages || 1));
        } else if (response.data) {

          setTotalUsers(response.data.length);
          setTotalPages(Math.ceil(response.data.length / ITEMS_PER_PAGE));
          setHasMore(response.data.length === ITEMS_PER_PAGE);
        }

      } else {
        console.error('Invalid response format:', response);
        if (filters.page === 1) {
          setUsers([]);
          setFilteredUsers([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (filters.page === 1) {
        setUsers([]);
        setFilteredUsers([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleScroll = useCallback(() => {
    const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
    const documentHeight = document.documentElement.offsetHeight;
    const scrollThreshold = 100; // pixels from bottom to trigger load

    if (documentHeight - scrollPosition < scrollThreshold) {

      if (!loadingMore && hasMore && page < totalPages) {
        const nextPage = page + 1;

        setPage(nextPage);
        fetchUsers({
          role: selectedRole,
          status: selectedStatus,
          search: debouncedSearch,
          vendor: selectedVendor,
          page: nextPage
        });
      }
    }
  }, [loadingMore, hasMore, page, totalPages, selectedRole, selectedStatus, debouncedSearch, selectedVendor]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleRoleChange = (e) => {
    const value = e.target.value;

    setSelectedRole(value);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;

    setSelectedStatus(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchUsers({
        role: selectedRole,
        status: selectedStatus,
        search: searchTerm,
        vendor: selectedVendor,
        page: 1
      });
    }
  };

  const handleClearFilters = () => {
    setSelectedStatus('');
    setSearchTerm('');
    setSelectedVendor('');
    setSelectedVendorName('');
    fetchUsers({
      role: selectedRole,
      status: '',
      search: '',
      vendor: '',
      page: 1
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const getRandomColor = () => {
    const colors = [
      '#FFE4E1', // Misty Rose
      '#E6E6FA', // Lavender
      '#F0FFF0', // Honeydew
      '#F0F8FF', // Alice Blue
      '#FFF0F5', // Lavender Blush
      '#F5F5DC', // Beige
      '#E0FFFF', // Light Cyan
      '#FFE4B5', // Moccasin
      '#F0FFFF', // Azure
      '#F8F8FF'  // Ghost White
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    let initials = names[0].charAt(0).toUpperCase();
    if (names.length > 1) {
      initials += names[names.length - 1].charAt(0).toUpperCase();
    }
    return initials;
  };

  const handleAddUser = () => {
    navigate('/users/add');
  };

  const handleViewProfile = (user) => {
    sessionStorage.removeItem('doctorDetails');
    navigate(`/users/view-profile/${user.id}`);


  };

  const filteredVendors = vendorNames;

  const handleVendorDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoadingVendors && vendorHasMore) {
      fetchVendorServices(vendorPage + 1, true);
    }
  };

  useEffect(() => {
    if (showVendorDropdown) {
      setVendorPage(1);
      setVendorHasMore(true);
      fetchVendorServices(1, false);
    }
  }, [debouncedVendorSearch]);

  return (
    <div className="container-fluid px-3" style={{ backgroundColor: '#F2F5FA' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 text-dark" style={{ fontSize: '24px', fontWeight: '600' }}>Users</h4>
        {loading ? (
          <div className="d-flex gap-2">
            <Skeleton width={140} height={40} />
            <Skeleton width={120} height={40} />
          </div>
        ) : (
          <div className="d-flex gap-3">
            <button
              className="btn btn-danger custom-btn  d-flex align-items-center gap-2"
              style={{ fontSize: '13px' }}
              onClick={handleAddUser}
            >
              <LuPlus size={18} /> Add User
            </button>
          </div>
        )}
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          {loading ? (
            <Skeleton height={40} />
          ) : (
            <div className="position-relative">
              <CiSearch
                className="position-absolute top-50 start-0 translate-middle-y ms-3"
              />
              <input
                type="text"
                className="form-control custom-input-search"
                placeholder="Search for users"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{
                  paddingRight: searchTerm ? '36px' : '12px',
                }}
              />
              {searchTerm && (
                <IoClose
                  className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"
                  style={{ cursor: 'pointer', fontSize: '20px' }}
                  onClick={handleClearSearch}
                />
              )}
            </div>
          )}
        </div>

        <div className="col d-flex gap-2">
          {loading ? (
            <>
              <Skeleton width={60} height={24} />
              <Skeleton width={140} height={40} />
              <Skeleton width={140} height={40} />
              <Skeleton width={140} height={40} />
            </>
          ) : (
            <>
              <div className="d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '400', color: '#000000' }}>Filter by:</div>

              {/* Vendor Filter Dropdown */}
              <div className="position-relative" style={{ width: '200px' }} ref={vendorDropdownRef}>
                <div
                  className="form-control d-flex justify-content-between align-items-center"
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    border: '1px solid #E3EBF8',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setShowVendorDropdown(!showVendorDropdown);
                    if (!showVendorDropdown) {
                      setVendorSearchTerm('');
                      setVendorPage(1);
                      setVendorHasMore(true);
                      fetchVendorServices(1, false);
                    }
                  }}
                >
                  <span className="text-dark">
                    {selectedVendor ? selectedVendorName : 'Select Vendor'}
                  </span>
                  <FaChevronDown className='text-dark' />
                </div>
                {showVendorDropdown && (
                  <div
                    className="position-absolute w-100 bg-white border rounded mt-1"
                    style={{
                      maxHeight: '300px',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div className="sticky-top bg-white border-bottom p-2">
                      <Form.Control
                        type="text"
                        placeholder="Search vendor..."
                        value={vendorSearchTerm}
                        onChange={(e) => {
                          setVendorSearchTerm(e.target.value);
                          setVendorPage(1);
                        }}
                        className="form-input"
                        style={{ height: '35px', fontSize: '14px' }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div
                      style={{ maxHeight: '240px', overflowY: 'auto' }}
                      onScroll={handleVendorDropdownScroll}
                    >
                      {vendorNames.length > 0 ? (
                        <>
                          {vendorNames.map((vendor) => (
                            <div
                              key={vendor.id}
                              className="px-3 py-2 cursor-pointer hover-bg-light"
                              style={{ cursor: 'pointer', fontSize: '14px' }}
                              onClick={() => {
                                setSelectedVendor(vendor.id);
                                setSelectedVendorName(vendor.name);
                                setVendorSearchTerm('');
                                setShowVendorDropdown(false);
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              {vendor.name}
                            </div>
                          ))}
                          {isLoadingVendors && (
                            <div className="px-3 py-2 text-center text-muted" style={{ fontSize: '14px' }}>
                              Loading more vendors...
                            </div>

                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 text-muted">
                          {isLoadingVendors ? 'Loading vendors...' : 'No vendors found'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Form.Select
                value={selectedStatus}
                onChange={handleStatusChange}
                className='custom-select  width-small'
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Select>
              {(selectedStatus || searchTerm || selectedVendor) && (
                <button
                  className="btn btn-link"
                  onClick={handleClearFilters}
                  style={{
                    height: '40px',
                    fontSize: '14px',
                    color: '#DC3545',
                    textDecoration: 'none'
                  }}
                >
                  Clear Filters
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton height={24} width={150} className="mb-4" />
      ) : (
        <p className="text-muted mb-4" style={{ fontSize: '14px', fontWeight: '400', color: '#6C757D' }}>
          Total Users: {totalUsers} (Page {page} of {totalPages})
        </p>
      )}

      <div className="row g-4" style={{ margin: '0px -8px', marginTop: '-30px' }}>
        {loading ? (
          Array(8).fill(0).map((_, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
              <UserCardSkeleton />
            </div>
          ))
        ) : (
          <>
            {filteredUsers.map(user => (
              <div key={user.id} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
                <div className="card h-100 shadow-sm" style={{
                  borderRadius: '8px',
                  border: '1px solid #E3EBF8',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div className="position-relative" style={{ padding: '12px', flex: '0 0 auto' }}>
                    {user.profile_image && user.profile_image !== 'undefined' ? (
                      <div style={{
                        width: '100%',
                        height: '110px',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}>
                        {/* Blurred background image */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            left: '-5px',
                            right: '-5px',
                            bottom: '-5px',
                            backgroundImage: `url(${user.profile_image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(8px)',
                            opacity: '0.7',
                          }}
                        />
                        {/* Main image */}
                        <div style={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}>
                          <img
                            src={user.profile_image}
                            alt={user.name}
                            className="profile-image"
                            onError={(e) => {
                              const parentDiv = e.target.parentElement.parentElement;
                              parentDiv.innerHTML = `
                              <div class="image-fallback" style="background-color: ${getRandomColor()}">
                             ${getInitials(user.name)}
                             </div>`;
                            }}
                          />

                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '110px',
                          backgroundColor: getRandomColor(),
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '48px',
                          color: '#666666',
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(user.name)}
                      </div>
                    )}
                  </div>
                  <div className="card-body p-3" style={{ flex: '1 1 auto', marginTop: '0px' }}>
                    <h5 className="card-title mb-1 doctor-name-title text-dark">
                      {user.serviceShortName}.{user.name.charAt(0).toUpperCase() + user.name.slice(1).toLowerCase()}
                    </h5>
                    <p className="doctor-info-text mb-0" >
                      {user.role_name.charAt(0).toUpperCase() + user.role_name.slice(1).toLowerCase()}
                    </p>

                    <p className="doctor-info-text mb-0" >
                      Vendor: <span>{user.vendor_name}</span>
                    </p>
                    <p className="doctor-info-text mb-2" >
                      {user.phone}
                    </p>
                    <div className="d-flex flex-column gap-2">
                      <button
                        className="btn btn-danger w-100"
                        style={{
                          height: '35px',
                          borderRadius: '16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          backgroundColor: '#C9194A'
                        }}
                        onClick={() => handleViewProfile(user)}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {loadingMore && (
              <>
                {Array(4).fill(0).map((_, index) => (
                  <div key={`skeleton-${index}`} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
                    <UserCardSkeleton />
                  </div>
                ))}
              </>
            )}
            {!hasMore && filteredUsers.length > 0 && (
              <div className="col-12 text-center py-3">
                <p className="text-muted">No more users to load</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;